import { supabase } from "@/lib/supabase/browser-client"
import { authenticatedFetch } from "@/lib/auth/authenticated-fetch"
import {
  buildSpaceTaskNotificationSnapshot,
  type SpaceTaskNotificationEvent,
  type SpaceTaskNotificationSnapshot,
  isCompletedSpaceTaskStatus
} from "@/lib/notifications/space-task-events"
import {
  canDispatchSpaceTaskNotificationEvents,
  dispatchSpaceTaskNotificationEvents
} from "@/lib/notifications/space-task-notification-client"
import {
  buildTaskCustomFields,
  getPersistedPrimaryTaskAssigneeId,
  normalizeTaskAssigneeIds,
  withTaskAssigneeIds
} from "@/lib/spaces/task-assignees"
import {
  filterSpaceTasksForUser,
  type SpaceTaskVisibilityContext,
  type SpaceTaskVisibilityScope
} from "@/lib/spaces/task-visibility"
import {
  getEmptyTaskCompletionProgress,
  getTaskCompletionProgressByTaskId,
  type TaskCompletionProgress
} from "@/lib/spaces/task-completion-progress"
import {
  isLosiTaskAssigneeId,
  buildLosiSyntheticTaskProfile,
  LOSI_TASK_ASSIGNEE_ID
} from "@/lib/spaces/losi-task-assignee"
import {
  buildTaskRecurrenceWriteFields,
  parseTaskRecurrenceRule
} from "@/lib/spaces/task-recurrence"
import { refreshSpaceSidebar } from "@/lib/spaces/sidebar-refresh"
import { safeConsole } from "@/lib/utils/safeConsole"
import {
  SpaceTaskAssigneeProfile,
  SpaceTaskInsert,
  SpaceTaskWithAssignees,
  SpaceTaskUpdate,
  TaskFilters,
  TaskSort,
  TaskStatus,
  TASK_PRIORITIES
} from "@/types/spaces"
import { SupabaseClient } from "@supabase/supabase-js"

const TASK_NOTIFICATION_SELECT = `
  id,
  space_id,
  title,
  status,
  assigned_to,
  assigned_by,
  folder_id,
  parent_task_id,
  priority,
  sprint_id,
  start_date,
  tags,
  watchers,
  custom_fields,
  due_date,
  user_id,
  updated_at,
  completed_at,
  archived_at
`

const TASK_RELATION_PROFILE_SELECT =
  "id, user_id, display_name, username, email, image_url"

const TASK_WITH_PRIMARY_ASSIGNEE_SELECT = `
  *,
  assignee:profiles!space_tasks_assigned_to_profile_fkey(${TASK_RELATION_PROFILE_SELECT}),
  assigner:profiles!space_tasks_assigned_by_profile_fkey(${TASK_RELATION_PROFILE_SELECT})
`

type HydratedSpaceTask<T extends SpaceTaskWithAssignees = SpaceTaskWithAssignees> =
  T & {
    assignees: SpaceTaskAssigneeProfile[]
  }

type SpaceTaskUpdateInput = SpaceTaskUpdate & {
  space_id?: string | null
}

export type DeleteTaskResult = {
  archived_at?: string | null
  success: true
  task?: SpaceTaskWithAssignees
  undo?: {
    body: { archived_at: null }
    method: "PATCH"
    path: string
  }
}

const normalizeUserId = (value: unknown) => {
  if (typeof value !== "string") {
    return null
  }

  const trimmedValue = value.trim()
  return trimmedValue.length > 0 ? trimmedValue : null
}

const normalizeAssigneeProfile = (
  profile: SpaceTaskAssigneeProfile | null | undefined,
  fallbackUserId?: string | null
): SpaceTaskAssigneeProfile | null => {
  const userId = normalizeUserId(profile?.user_id ?? fallbackUserId)
  const profileId = normalizeUserId(profile?.id)

  if (!userId && !profileId) {
    return null
  }

  return {
    id: profileId,
    user_id: userId ?? profileId,
    display_name:
      typeof profile?.display_name === "string" ? profile.display_name : null,
    username: typeof profile?.username === "string" ? profile.username : null,
    email: typeof profile?.email === "string" ? profile.email : null,
    image_url:
      typeof profile?.image_url === "string" ? profile.image_url : null
  }
}

export const hydrateTaskAssignees = async <T extends SpaceTaskWithAssignees>(
  tasks: T[],
  client: SupabaseClient = supabase
): Promise<HydratedSpaceTask<T>[]> => {
  if (tasks.length === 0) {
    return []
  }

  const assigneeIds = Array.from(
    new Set(
      tasks.flatMap(task =>
        normalizeTaskAssigneeIds(task.assignee_ids, task.assigned_to ?? null)
      )
    )
  )
  const profileAssigneeIds = assigneeIds.filter(
    assigneeId => !isLosiTaskAssigneeId(assigneeId)
  )

  const profilesByUserId = new Map<string, SpaceTaskAssigneeProfile>()

  if (profileAssigneeIds.length > 0) {
    const { data: profileRows, error: profileError } = await client
      .from("profiles")
      .select(TASK_RELATION_PROFILE_SELECT)
      .in("user_id", profileAssigneeIds)

    if (profileError) {
      safeConsole.warn("Failed to hydrate space task assignees", {
        error: profileError,
        assigneeCount: profileAssigneeIds.length
      })
    } else {
      for (const profile of profileRows || []) {
        const normalizedProfile = normalizeAssigneeProfile(
          profile as SpaceTaskAssigneeProfile
        )
        const userId = normalizeUserId(normalizedProfile?.user_id)

        if (!normalizedProfile || !userId) {
          continue
        }

        profilesByUserId.set(userId, normalizedProfile)
      }
    }
  }

  return tasks.map(task => {
    const orderedAssignees: SpaceTaskAssigneeProfile[] = []
    const seenAssigneeIds = new Set<string>()
    const taskAssigneeIds = normalizeTaskAssigneeIds(
      task.assignee_ids,
      task.assigned_to ?? null
    )

    const pushAssignee = (
      profile: SpaceTaskAssigneeProfile | null | undefined,
      fallbackUserId?: string | null
    ) => {
      const normalizedProfile = normalizeAssigneeProfile(profile, fallbackUserId)
      const userId = normalizeUserId(normalizedProfile?.user_id)

      if (!normalizedProfile || !userId || seenAssigneeIds.has(userId)) {
        return
      }

      seenAssigneeIds.add(userId)
      orderedAssignees.push(normalizedProfile)
    }

    pushAssignee(task.assignee, task.assigned_to ?? null)

    for (const assigneeId of taskAssigneeIds) {
      pushAssignee(profilesByUserId.get(assigneeId), assigneeId)
    }

    if (Array.isArray(task.assignees)) {
      for (const assignee of task.assignees) {
        pushAssignee(assignee)
      }
    }

    return {
      ...task,
      assignees: taskAssigneeIds.some(isLosiTaskAssigneeId)
        ? [
            ...orderedAssignees.filter(
              a => a.user_id !== LOSI_TASK_ASSIGNEE_ID
            ),
            buildLosiSyntheticTaskProfile()
          ]
        : orderedAssignees
    }
  })
}

export const hydrateTaskAssignee = async <T extends SpaceTaskWithAssignees>(
  task: T,
  client: SupabaseClient = supabase
): Promise<HydratedSpaceTask<T>> => {
  const [hydratedTask] = await hydrateTaskAssignees([task], client)
  return hydratedTask
}

const getCurrentUserId = async (client: SupabaseClient) => {
  const {
    data: { user }
  } = await client.auth.getUser()

  return user?.id ?? null
}

const getExistingTaskNotificationSnapshot = async (
  client: SupabaseClient,
  taskId: string
) => {
  const { data, error } = await client
    .from("space_tasks")
    .select(TASK_NOTIFICATION_SELECT)
    .eq("id", taskId)
    .maybeSingle()

  if (error) {
    throw error
  }

  return data ? buildSpaceTaskNotificationSnapshot(data) : null
}

const getTaskRouteErrorMessage = (payload: unknown) => {
  if (typeof payload === "string" && payload.trim().length > 0) {
    return payload
  }

  if (
    typeof payload === "object" &&
    payload !== null &&
    "error" in payload &&
    typeof payload.error === "string"
  ) {
    return payload.error
  }

  return "Failed to update task"
}

export class SpaceTaskRouteError extends Error {
  payload: unknown

  constructor(message: string, payload: unknown) {
    super(message)
    this.name = "SpaceTaskRouteError"
    this.payload = payload
  }
}

const readTaskRoutePayload = async (response: Response) => {
  const responseText = await response.text()

  if (!responseText) {
    return null
  }

  try {
    return JSON.parse(responseText) as unknown
  } catch {
    return responseText
  }
}

const isTaskPayload = (payload: unknown): payload is SpaceTaskWithAssignees => {
  if (typeof payload !== "object" || payload === null) {
    return false
  }

  return "id" in payload && "space_id" in payload
}

const updateTaskThroughApiRoute = async (
  spaceId: string,
  taskId: string,
  updates: SpaceTaskUpdate
): Promise<SpaceTaskWithAssignees> => {
  const response = await authenticatedFetch(`/api/spaces/${spaceId}/tasks/${taskId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(updates)
  })

  const payload = (await response.json().catch(() => null)) as unknown

  if (!response.ok) {
    throw new Error(getTaskRouteErrorMessage(payload))
  }

  if (!isTaskPayload(payload)) {
    throw new Error("Invalid task payload returned from API")
  }

  return hydrateTaskAssignee(
    withTaskAssigneeIds(payload)
  )
}

const getTaskByIdThroughApiRoute = async (
  spaceId: string,
  taskId: string
): Promise<SpaceTaskWithAssignees> => {
  const response = await authenticatedFetch(`/api/spaces/${spaceId}/tasks/${taskId}`, {
    cache: "no-store"
  })

  const payload = await readTaskRoutePayload(response)

  if (!response.ok) {
    throw new Error(getTaskRouteErrorMessage(payload))
  }

  if (!isTaskPayload(payload)) {
    throw new Error("Invalid task payload returned from API")
  }

  return hydrateTaskAssignee(withTaskAssigneeIds(payload))
}

const resolveBrowserSpaceId = (updates: SpaceTaskUpdateInput) => {
  if (typeof updates.space_id === "string" && updates.space_id.trim().length > 0) {
    return updates.space_id
  }

  if (typeof window === "undefined") {
    return null
  }

  const pathnameMatch = window.location.pathname.match(
    /\/spaces\/([0-9a-f-]{36})/i
  )

  return pathnameMatch?.[1] ?? null
}

const resolveBrowserSpaceIdFromPathname = () => {
  if (typeof window === "undefined") {
    return null
  }

  const pathnameMatch = window.location.pathname.match(
    /\/spaces\/([0-9a-f-]{36})/i
  )

  return pathnameMatch?.[1] ?? null
}

const appendSearchParams = (
  params: URLSearchParams,
  key: string,
  values: string[] | undefined
) => {
  for (const value of values || []) {
    const trimmedValue = value.trim()

    if (trimmedValue.length > 0) {
      params.append(key, trimmedValue)
    }
  }
}

const buildSpaceTasksRoute = (
  spaceId: string,
  filters?: TaskFilters,
  sort?: TaskSort,
  includeArchived?: boolean,
  includeCompletion: boolean = true,
  taskScope: SpaceTaskVisibilityScope = "me"
) => {
  const params = new URLSearchParams()

  appendSearchParams(params, "status", filters?.status)
  appendSearchParams(params, "priority", filters?.priority)
  appendSearchParams(params, "assigned_to", filters?.assigned_to)
  appendSearchParams(params, "assignee_id", filters?.assignee_ids)

  if (filters?.sprint_id !== undefined) {
    params.set("sprint_id", filters.sprint_id ?? "__none__")
  }

  if (filters?.has_due_date) {
    params.set("has_due_date", "true")
  }

  if (filters?.include_overdue) {
    params.set("include_overdue", "true")
  }

  if (filters?.is_overdue) {
    params.set("is_overdue", "true")
  }

  if (filters?.planner_range_start) {
    params.set("planner_range_start", filters.planner_range_start)
  }

  if (filters?.planner_range_end) {
    params.set("planner_range_end", filters.planner_range_end)
  }

  if (filters?.search?.trim()) {
    params.set("search", filters.search.trim())
  }

  if (includeArchived) {
    params.set("include_archived", "true")
  }

  if (!includeCompletion) {
    params.set("include_completion", "false")
  }

  params.set("task_scope", taskScope)

  if (sort) {
    params.set("sort_field", sort.field)
    params.set("sort_direction", sort.direction)
  }

  const queryString = params.toString()
  return `/api/spaces/${spaceId}/tasks${queryString ? `?${queryString}` : ""}`
}

const getSpaceTasksThroughApiRoute = async (
  spaceId: string,
  filters?: TaskFilters,
  sort?: TaskSort,
  includeArchived?: boolean,
  includeCompletion: boolean = true,
  taskScope: SpaceTaskVisibilityScope = "me"
) => {
  const response = await authenticatedFetch(
    buildSpaceTasksRoute(
      spaceId,
      filters,
      sort,
      includeArchived,
      includeCompletion,
      taskScope
    ),
    {
      cache: "no-store"
    }
  )
  const payload = await readTaskRoutePayload(response)

  if (!response.ok) {
    throw new Error(getTaskRouteErrorMessage(payload))
  }

  if (!Array.isArray(payload)) {
    throw new Error("Invalid tasks payload returned from API")
  }

  return payload as SpaceTaskWithAssignees[]
}

const appendTaskCompletionProgress = async <
  T extends { id: string }
>(
  spaceId: string,
  tasks: T[],
  client: SupabaseClient = supabase
): Promise<Array<T & {
  subtask_items: number
  subtask_completed: number
  checklist_items: number
  checklist_completed: number
  completion_total: number
  completion_done: number
  completion_percent: number
}>> => {
  if (tasks.length === 0) {
    return [] as Array<T & {
      subtask_items: number
      subtask_completed: number
      checklist_items: number
      checklist_completed: number
      completion_total: number
      completion_done: number
      completion_percent: number
    }>
  }

  const taskIds = tasks.map(task => task.id)
  let progressByTaskId = new Map<string, TaskCompletionProgress>()

  try {
    progressByTaskId = await getTaskCompletionProgressByTaskId(
      client,
      spaceId,
      taskIds
    )
  } catch (error) {
    safeConsole.warn("Failed to load task completion progress", {
      error,
      taskCount: taskIds.length
    })
  }

  return tasks.map(task => {
    const progress =
      progressByTaskId.get(task.id) || getEmptyTaskCompletionProgress()
    const completionTotal =
      progress.subtask_items + progress.checklist_items
    const completionDone =
      progress.subtask_completed + progress.checklist_completed
    const completionPercent =
      completionTotal === 0
        ? 0
        : Math.round((completionDone / completionTotal) * 100)

    return {
      ...task,
      subtask_items: progress.subtask_items,
      subtask_completed: progress.subtask_completed,
      checklist_items: progress.checklist_items,
      checklist_completed: progress.checklist_completed,
      completion_total: completionTotal,
      completion_done: completionDone,
      completion_percent: completionPercent
    }
  })
}

const resolveTaskAssigneeFilterIds = (filters?: TaskFilters) => {
  if (!filters) {
    return []
  }

  return normalizeTaskAssigneeIds(
    filters.assignee_ids ?? filters.assigned_to ?? [],
    null
  )
}

const getPlannerRangeTaskFilter = (filters?: TaskFilters) => {
  const start = filters?.planner_range_start
  const end = filters?.planner_range_end
  const clauses: string[] = []

  if (start && end) {
    clauses.push(
      `and(start_date.lte.${end},due_date.gte.${start})`,
      `and(start_date.gte.${start},start_date.lte.${end})`,
      `and(due_date.gte.${start},due_date.lte.${end})`
    )
  }

  if (filters?.include_overdue) {
    clauses.push(`due_date.lt.${new Date().toISOString()}`)
  }

  return clauses.length > 0 ? clauses.join(",") : null
}

export const taskMatchesAssigneeFilters = (
  task: Pick<SpaceTaskWithAssignees, "assigned_to" | "assignee_ids" | "custom_fields">,
  assigneeFilterIds: string[]
) => {
  if (assigneeFilterIds.length === 0) {
    return true
  }

  const taskAssigneeIds = normalizeTaskAssigneeIds(
    task.assignee_ids,
    task.assigned_to ?? null
  )

  return taskAssigneeIds.some(taskAssigneeId =>
    assigneeFilterIds.includes(taskAssigneeId)
  )
}

export const getSpaceTasks = async (
  spaceId: string,
  filters?: TaskFilters,
  sort?: TaskSort,
  includeArchived?: boolean,
  includeCompletion: boolean = true,
  client: SupabaseClient = supabase,
  visibility?: SpaceTaskVisibilityContext | null,
  taskScope: SpaceTaskVisibilityScope = "me",
  // Opt-in paging for continuous scroll (native clients). Absent = the
  // historical full list, so web callers are untouched.
  page?: { limit: number; offset: number }
) => {
  if (typeof window !== "undefined" && client === supabase) {
    return getSpaceTasksThroughApiRoute(
      spaceId,
      filters,
      sort,
      includeArchived,
      includeCompletion,
      taskScope
    )
  }

  let query = client
    .from("space_tasks")
    .select(TASK_WITH_PRIMARY_ASSIGNEE_SELECT)
    .eq("space_id", spaceId)
  
  // Exclude archived tasks by default unless includeArchived is true
  if (!includeArchived) {
    query = query.is("archived_at", null)
  }

  // Apply filters
  if (filters) {
    if (filters.status && filters.status.length > 0) {
      query = query.in("status", filters.status)
    }
    if (filters.priority && filters.priority.length > 0) {
      query = query.in("priority", filters.priority)
    }
    if (filters.sprint_id !== undefined) {
      if (filters.sprint_id === null) {
        query = query.is("sprint_id", null)
      } else {
        query = query.eq("sprint_id", filters.sprint_id)
      }
    }
    if (filters.has_due_date) {
      query = query.not("due_date", "is", null)
    }
    if (filters.is_overdue) {
      query = query
        .not("due_date", "is", null)
        .lt("due_date", new Date().toISOString())
        .not("status", "in", '("done","cancelled")')
    }
    if (filters.search) {
      query = query.textSearch("search_vector", filters.search)
    }

    const plannerRangeFilter = getPlannerRangeTaskFilter(filters)
    if (plannerRangeFilter) {
      query = query.or(plannerRangeFilter)
    }
  }

  // Apply sort
  if (sort) {
    query = query.order(sort.field, { ascending: sort.direction === "asc" })
  } else {
    query = query.order("position").order("created_at", { ascending: false })
  }

  // SQL-range only when nothing below re-filters in JS; otherwise the
  // page is sliced after processing so pages never come back short.
  const assigneeFilterIds = resolveTaskAssigneeFilterIds(filters)
  const canSqlPage = Boolean(page) && assigneeFilterIds.length === 0 && !visibility
  if (page && canSqlPage) {
    query = query.range(page.offset, page.offset + page.limit - 1)
  }

  const { data, error } = await query

  if (error) throw error

  if (data?.length) {
    const archivedCount = data.filter((t: { archived_at: unknown }) => t.archived_at != null).length
    if (archivedCount > 0) {
      safeConsole.warn('getSpaceTasks returned archived task(s); filter may not be applied', {
        spaceId,
        archivedCount
      })
    }
  }

  const tasks = ((data || []) as SpaceTaskWithAssignees[])
    .map(task => withTaskAssigneeIds(task))
    .filter(task => taskMatchesAssigneeFilters(task, assigneeFilterIds))
  const visibleTasks = visibility
    ? filterSpaceTasksForUser(tasks, visibility)
    : tasks
  const hydratedTasks = await hydrateTaskAssignees(visibleTasks, client)

  const pagedTasks =
    page && !canSqlPage
      ? hydratedTasks.slice(page.offset, page.offset + page.limit)
      : hydratedTasks

  if (!includeCompletion) {
    return pagedTasks
  }

  return appendTaskCompletionProgress(
    spaceId,
    pagedTasks as Array<{ id: string }>,
    client
  )
}

export const getTaskById = async (
  taskId: string,
  spaceId?: string | null
): Promise<SpaceTaskWithAssignees> => {
  if (typeof window !== "undefined") {
    const routeSpaceId = spaceId || resolveBrowserSpaceIdFromPathname()

    if (routeSpaceId) {
      return getTaskByIdThroughApiRoute(routeSpaceId, taskId)
    }

    throw new Error("Space id is required to load tasks from the browser")
  }

  const { data, error } = await supabase
    .from("space_tasks")
    .select(
      `
      ${TASK_WITH_PRIMARY_ASSIGNEE_SELECT},
      subtasks:space_tasks!parent_task_id(*)
    `
    )
    .eq("id", taskId)
    .single()

  if (error) throw error
  return hydrateTaskAssignee(
    withTaskAssigneeIds(data as SpaceTaskWithAssignees)
  )
}

export const createTask = async (task: SpaceTaskInsert, supabaseClient?: SupabaseClient) => {
  if (!supabaseClient && typeof window !== "undefined") {
    const response = await authenticatedFetch(`/api/spaces/${task.space_id}/tasks`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(task)
    })

    const payload = await readTaskRoutePayload(response)

    if (!response.ok) {
      throw new SpaceTaskRouteError(getTaskRouteErrorMessage(payload), payload)
    }

    if (!isTaskPayload(payload)) {
      throw new Error("Invalid task payload returned from API")
    }

    const createdTask = await hydrateTaskAssignee(
      withTaskAssigneeIds(payload)
    )

    if (createdTask.space_id) {
      refreshSpaceSidebar(createdTask.space_id)
    }

    return createdTask
  }

  // Use provided client or fall back to browser client
  const client = supabaseClient || supabase
  
  // Extract department, project, task_type and store in custom_fields
  const { assignee_ids, department, project, task_type, ...taskData } = task
  
  const finalTask: any = { ...taskData }
  const recurrenceRule = parseTaskRecurrenceRule(finalTask.recurrence_rule)

  if (Object.prototype.hasOwnProperty.call(finalTask, "recurrence_rule")) {
    Object.assign(
      finalTask,
      buildTaskRecurrenceWriteFields(recurrenceRule, {
        startDate: finalTask.start_date,
        dueDate: finalTask.due_date
      })
    )
  }

  const normalizedAssigneeIds = normalizeTaskAssigneeIds(
    assignee_ids,
    taskData.assigned_to ?? null
  )

  finalTask.assigned_to =
    getPersistedPrimaryTaskAssigneeId(normalizedAssigneeIds)

  const taskCustomFields = buildTaskCustomFields({
    assigneeIds: normalizedAssigneeIds,
    department,
    project,
    taskType: task_type
  })

  if (taskCustomFields) {
    finalTask.custom_fields = taskCustomFields
  }

  if (finalTask.assigned_to) {
    finalTask.assigned_by = task.user_id
  }

  const { data, error } = await client
    .from("space_tasks")
    .insert(finalTask)
    .select(TASK_WITH_PRIMARY_ASSIGNEE_SELECT)
    .single()

  if (error) throw error

  const createdTask = await hydrateTaskAssignee(
    withTaskAssigneeIds(data as SpaceTaskWithAssignees),
    client
  )

  if (canDispatchSpaceTaskNotificationEvents()) {
    void dispatchSpaceTaskNotificationEvents([
      {
        after: buildSpaceTaskNotificationSnapshot(createdTask)
      }
    ])
  }

  if (createdTask.space_id) {
    refreshSpaceSidebar(createdTask.space_id)
  }

  return createdTask
}

export const updateTask = async (taskId: string, updates: SpaceTaskUpdateInput) => {
  // Extract department, project, task_type and handle in custom_fields
  const {
    assignee_ids,
    department,
    project,
    task_type,
    ...updateData
  } = updates as SpaceTaskUpdate & { assignee_ids?: string[] | null }
  
  const finalUpdates: any = { ...updateData }
  const recurrenceRule = parseTaskRecurrenceRule(finalUpdates.recurrence_rule)

  if (Object.prototype.hasOwnProperty.call(finalUpdates, "recurrence_rule")) {
    Object.assign(
      finalUpdates,
      buildTaskRecurrenceWriteFields(recurrenceRule, {
        startDate: finalUpdates.start_date ?? undefined,
        dueDate: finalUpdates.due_date ?? undefined,
        recurrenceNextAt: finalUpdates.recurrence_next_at ?? undefined
      })
    )
  }
  const hasAssignedToUpdate = Object.prototype.hasOwnProperty.call(
    finalUpdates,
    "assigned_to"
  )
  const nextAssigneeIds =
    assignee_ids !== undefined
      ? normalizeTaskAssigneeIds(
          assignee_ids,
          hasAssignedToUpdate ? finalUpdates.assigned_to ?? null : null
        )
      : hasAssignedToUpdate
        ? normalizeTaskAssigneeIds([], finalUpdates.assigned_to ?? null)
        : undefined

  if (nextAssigneeIds !== undefined) {
    finalUpdates.assigned_to =
      getPersistedPrimaryTaskAssigneeId(nextAssigneeIds)
  }

  if (finalUpdates.priority !== undefined && finalUpdates.priority !== null) {
    const p = String(finalUpdates.priority).toLowerCase()
    if (!TASK_PRIORITIES.includes(p as (typeof TASK_PRIORITIES)[number])) {
      finalUpdates.priority = "medium"
    } else {
      finalUpdates.priority = p
    }
  }

  if (typeof window !== "undefined") {
    const spaceId = resolveBrowserSpaceId(updates)

    if (!spaceId) {
      throw new Error("Space id is required to update tasks from the browser")
    }

    const routeUpdates: SpaceTaskUpdate =
      nextAssigneeIds !== undefined
        ? {
            ...finalUpdates,
            assignee_ids: nextAssigneeIds
          }
        : finalUpdates

    if (department !== undefined) {
      routeUpdates.department = department
    }

    if (project !== undefined) {
      routeUpdates.project = project
    }

    if (task_type !== undefined) {
      routeUpdates.task_type = task_type
    }

    if ("space_id" in routeUpdates) {
      delete (routeUpdates as Record<string, unknown>).space_id
    }

    try {
      const data = await updateTaskThroughApiRoute(
        spaceId,
        taskId,
        routeUpdates
      )

      const shouldRefreshSidebar =
        "title" in finalUpdates ||
        "space_id" in updates ||
        "folder_id" in finalUpdates ||
        "sprint_id" in finalUpdates ||
        "archived_at" in finalUpdates

      if (shouldRefreshSidebar && data?.space_id) {
        refreshSpaceSidebar(data.space_id)
      }

      return data
    } catch (error) {
      safeConsole.warn("Task update route failed; refusing direct browser fallback", {
        taskId,
        spaceId,
        error
      })
      throw error
    }
  }

  const shouldCaptureNotificationEvent =
    canDispatchSpaceTaskNotificationEvents() &&
    [
      "assigned_to",
      "status",
      "due_date",
      "archived_at"
    ].some(key => Object.prototype.hasOwnProperty.call(finalUpdates, key))
  const beforeNotificationSnapshot = shouldCaptureNotificationEvent
    ? await getExistingTaskNotificationSnapshot(supabase, taskId)
    : null

  // Handle department, project, and task_type in custom_fields
  if (
    department !== undefined ||
    project !== undefined ||
    task_type !== undefined ||
    nextAssigneeIds !== undefined
  ) {
    // Fetch existing custom_fields to merge
    const { data: existingTask } = await supabase
      .from("space_tasks")
      .select("custom_fields")
      .eq("id", taskId)
      .single()

    const existingCustomFields = Object.prototype.hasOwnProperty.call(
      finalUpdates,
      "custom_fields"
    )
      ? finalUpdates.custom_fields
      : existingTask?.custom_fields

    finalUpdates.custom_fields =
      buildTaskCustomFields({
        assigneeIds: nextAssigneeIds,
        department,
        existingCustomFields,
        project,
        taskType: task_type
      }) || {}
  }

  const shouldRefreshSidebar =
    "title" in finalUpdates ||
    "space_id" in finalUpdates ||
    "folder_id" in finalUpdates ||
    "sprint_id" in finalUpdates ||
    "archived_at" in finalUpdates

  if (Object.prototype.hasOwnProperty.call(finalUpdates, "status")) {
    if (isCompletedSpaceTaskStatus(finalUpdates.status)) {
      finalUpdates.completed_at = new Date().toISOString()
    } else {
      finalUpdates.completed_at = null
    }
  }

  if (Object.prototype.hasOwnProperty.call(finalUpdates, "assigned_to")) {
    const currentUserId = await getCurrentUserId(supabase)
    finalUpdates.assigned_by =
      finalUpdates.assigned_to && currentUserId ? currentUserId : null
  }

  const { data, error } = await supabase
    .from("space_tasks")
    .update(finalUpdates)
    .eq("id", taskId)
    .select(TASK_WITH_PRIMARY_ASSIGNEE_SELECT)
    .single()

  if (error) throw error

  const updatedTask = await hydrateTaskAssignee(
    withTaskAssigneeIds(data as SpaceTaskWithAssignees)
  )

  if (shouldRefreshSidebar && updatedTask?.space_id) {
    refreshSpaceSidebar(updatedTask.space_id)
  }

  if (shouldCaptureNotificationEvent && beforeNotificationSnapshot) {
    void dispatchSpaceTaskNotificationEvents([
      {
        before: beforeNotificationSnapshot,
        after: buildSpaceTaskNotificationSnapshot(updatedTask)
      }
    ])
  }

  return updatedTask
}

export const updateTaskStatus = async (
  taskId: string,
  status: TaskStatus,
  spaceId?: string | null
) => {
  return updateTask(taskId, {
    ...(spaceId ? { space_id: spaceId } : {}),
    status
  })
}

export const updateTaskPosition = async (
  taskId: string,
  position: number,
  version: number,
  spaceId?: string | null
) => {
  if (typeof window !== "undefined") {
    const routeSpaceId = spaceId || resolveBrowserSpaceIdFromPathname()

    if (routeSpaceId) {
      const response = await authenticatedFetch(
        `/api/spaces/${routeSpaceId}/tasks/${taskId}?expectedVersion=${version}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            position
          })
        }
      )

      const payload = await readTaskRoutePayload(response)

      if (!response.ok) {
        throw new Error(getTaskRouteErrorMessage(payload))
      }

      if (!isTaskPayload(payload)) {
        throw new Error("Invalid task payload returned from API")
      }

      return withTaskAssigneeIds(payload)
    }
  }

  const { data, error } = await supabase
    .from("space_tasks")
    .update({
      position,
      version: version + 1
    })
    .eq("id", taskId)
    .eq("version", version) // Optimistic locking
    .select()
    .single()

  if (error) {
    if (error.code === "PGRST116") {
      // Version conflict
      throw new Error("Task was modified by another user. Please refresh.")
    }
    throw error
  }

  return data
}

export const deleteTask = async (taskId: string): Promise<DeleteTaskResult> => {
  if (typeof window !== "undefined") {
    const spaceId = resolveBrowserSpaceIdFromPathname()

    if (spaceId) {
      const response = await authenticatedFetch(`/api/spaces/${spaceId}/tasks/${taskId}`, {
        method: "DELETE"
      })

      const payload = await readTaskRoutePayload(response)
      if (!response.ok) {
        throw new Error(getTaskRouteErrorMessage(payload))
      }

      return (payload && typeof payload === "object"
        ? payload
        : { success: true }) as DeleteTaskResult
    }
  }

  const archivedAt = new Date().toISOString()
  const { data, error } = await supabase
    .from("space_tasks")
    .update({
      archived_at: archivedAt,
      updated_at: archivedAt
    })
    .eq("id", taskId)
    .is("archived_at", null)
    .select()
    .maybeSingle()

  if (error) throw error
  if (!data) throw new Error("Task not found")

  return {
    archived_at: archivedAt,
    success: true,
    task: withTaskAssigneeIds(data)
  }
}

export const restoreTask = async (taskId: string, spaceId?: string) => {
  const routeSpaceId =
    typeof window !== "undefined"
      ? spaceId || resolveBrowserSpaceIdFromPathname()
      : spaceId

  if (typeof window !== "undefined" && routeSpaceId) {
    const response = await authenticatedFetch(`/api/spaces/${routeSpaceId}/tasks/${taskId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ archived_at: null })
    })

    const payload = await readTaskRoutePayload(response)
    if (!response.ok) {
      throw new Error(getTaskRouteErrorMessage(payload))
    }

    return payload as SpaceTaskWithAssignees
  }

  const { data, error } = await supabase
    .from("space_tasks")
    .update({
      archived_at: null,
      updated_at: new Date().toISOString()
    })
    .eq("id", taskId)
    .select()
    .single()

  if (error) throw error

  return withTaskAssigneeIds(data)
}

export const getTasksBySprintId = async (sprintId: string) => {
  const { data, error } = await supabase
    .from("space_tasks")
    .select(TASK_WITH_PRIMARY_ASSIGNEE_SELECT)
    .eq("sprint_id", sprintId)
    .order("position")
    .order("created_at", { ascending: false })

  if (error) throw error
  return hydrateTaskAssignees(
    (data || []).map(task =>
      withTaskAssigneeIds(task as SpaceTaskWithAssignees)
    )
  )
}

export const assignTask = async (taskId: string, userId: string | null) => {
  return updateTask(taskId, { assigned_to: userId })
}

export const getMyTasks = async (userId: string, spaceId?: string) => {
  let query = supabase
    .from("space_tasks")
    .select(TASK_WITH_PRIMARY_ASSIGNEE_SELECT)
    .not("status", "in", '("done","cancelled")')

  if (spaceId) {
    query = query.eq("space_id", spaceId)
  }

  query = query.order("due_date", { ascending: true, nullsFirst: false })

  const { data, error } = await query

  if (error) throw error
  const tasks = (data || [])
    .map(task => withTaskAssigneeIds(task as SpaceTaskWithAssignees))
    .filter(task => taskMatchesAssigneeFilters(task, [userId]))

  return hydrateTaskAssignees(tasks)
}

export const getOverdueTasks = async (spaceId: string) => {
  const { data, error } = await supabase
    .from("space_tasks")
    .select(TASK_WITH_PRIMARY_ASSIGNEE_SELECT)
    .eq("space_id", spaceId)
    .not("status", "in", '("done","cancelled")')
    .not("due_date", "is", null)
    .lt("due_date", new Date().toISOString())
    .order("due_date")

  if (error) throw error
  return hydrateTaskAssignees(
    (data || []).map(task =>
      withTaskAssigneeIds(task as SpaceTaskWithAssignees)
    )
  )
}

export const bulkUpdateTaskStatus = async (
  taskIds: string[],
  status: TaskStatus
) => {
  const beforeSnapshots = canDispatchSpaceTaskNotificationEvents()
    ? await supabase
        .from("space_tasks")
        .select(TASK_NOTIFICATION_SELECT)
        .in("id", taskIds)
    : null

  const updatePayload: Record<string, unknown> = { status }

  if (isCompletedSpaceTaskStatus(status)) {
    updatePayload.completed_at = new Date().toISOString()
  } else {
    updatePayload.completed_at = null
  }

  const { data, error } = await supabase
    .from("space_tasks")
    .update(updatePayload)
    .in("id", taskIds)
    .select(TASK_NOTIFICATION_SELECT)

  if (error) throw error

  if (
    canDispatchSpaceTaskNotificationEvents() &&
    beforeSnapshots?.data &&
    data
  ) {
    const beforeById = new Map(
      beforeSnapshots.data.map(task => [
        task.id,
        buildSpaceTaskNotificationSnapshot(task)
      ])
    )

    const events = data
      .map(task => {
        const before = beforeById.get(task.id)
        if (!before) {
          return null
        }

        return {
          before,
          after: buildSpaceTaskNotificationSnapshot(task)
        }
      })
      .filter((event): event is SpaceTaskNotificationEvent => event !== null)

    void dispatchSpaceTaskNotificationEvents(events)
  }
}

export const bulkAssignTasks = async (taskIds: string[], userId: string) => {
  const beforeSnapshots = canDispatchSpaceTaskNotificationEvents()
    ? await supabase
        .from("space_tasks")
        .select(TASK_NOTIFICATION_SELECT)
        .in("id", taskIds)
    : null
  const currentUserId = await getCurrentUserId(supabase)
  const { data: existingTasks, error: existingTasksError } = await supabase
    .from("space_tasks")
    .select("id, custom_fields")
    .in("id", taskIds)

  if (existingTasksError) throw existingTasksError

  const normalizedAssigneeIds = normalizeTaskAssigneeIds([], userId)
  const data = await Promise.all(
    (existingTasks || []).map(async task => {
      const { data: updatedTask, error } = await supabase
        .from("space_tasks")
        .update({
          assigned_by: currentUserId,
          assigned_to: userId,
          custom_fields:
            buildTaskCustomFields({
              assigneeIds: normalizedAssigneeIds,
              existingCustomFields: task.custom_fields
            }) || {}
        })
        .eq("id", task.id)
        .select(TASK_NOTIFICATION_SELECT)
        .single()

      if (error) {
        throw error
      }

      return updatedTask
    })
  )

  if (
    canDispatchSpaceTaskNotificationEvents() &&
    beforeSnapshots?.data &&
    data
  ) {
    const beforeById = new Map(
      beforeSnapshots.data.map(task => [
        task.id,
        buildSpaceTaskNotificationSnapshot(task)
      ])
    )

    const events = data
      .map(task => {
        const before = beforeById.get(task.id)
        if (!before) {
          return null
        }

        return {
          before,
          after: buildSpaceTaskNotificationSnapshot(task)
        }
      })
      .filter((event): event is SpaceTaskNotificationEvent => event !== null)

    void dispatchSpaceTaskNotificationEvents(events)
  }
}
